// h5ad-viewer\src\components\H5ADReadPanel.jsx
import React, { useState, useEffect, useRef } from "react";

const API_BASE = process.env.REACT_APP_API_BASE;

function H5ADReadPanel({ handle, metadata, onResult, setReadStatus, setReadDuration }) {
  const [queryUnits, setQueryUnits] = useState([{ key: "", value: "", selected: [] }]);
  const [suggestions, setSuggestions] = useState([]);
  const [activeUnitIndex, setActiveUnitIndex] = useState(null);
  const blurTimeoutRef = useRef(null);
  const [allMatches, setAllMatches] = useState([]);    // full match list
  const [visibleCount, setVisibleCount] = useState(20); // visible chunk
  const [rowLimit, setRowLimit] = useState(50); // Default to 50

  const getAvailableKeys = (metadata) => {
    const keys = [];
    if (metadata?.cell_names) keys.push("cell_names");
    if (metadata?.gene_names) keys.push("gene_names");
    if (metadata?.obs_keys) keys.push(...metadata.obs_keys);
    if (metadata?.var_keys) keys.push(...metadata.var_keys);
    return keys;
  };

  const availableKeys = getAvailableKeys(metadata);

  const getSourceValues = (key) => {
    if (key === "cell_names") return metadata?.cell_names;
    if (key === "gene_names") return metadata?.gene_names;
    if (metadata?.obs_values?.[key]) return metadata.obs_values[key];
    if (metadata?.var_values?.[key]) return metadata.var_values[key];
    return [];
  };

  useEffect(() => {
    console.log("📦 [H5ADReadPanel] Metadata loaded:");
    console.log("🔑 Available keys:", getAvailableKeys(metadata));
    console.log("🔁 Obs key values:", metadata?.obs_values);
    console.log("🧬 Var key values:", metadata?.var_values);
  }, [metadata]);

  const handleKeyChange = (index, newKey) => {
    const updated = [...queryUnits];
    updated[index].key = newKey;
    updated[index].value = "";
    updated[index].selected = [];
    setQueryUnits(updated);
    setSuggestions([]);
    console.log(`🔁 [QueryUnit ${index}] Key changed to "${newKey}"`);
  };

  const handleValueChange = (index, newValue) => {
    const updated = [...queryUnits];
    updated[index].value = newValue;
    setQueryUnits(updated);
    setActiveUnitIndex(index);

    updateSuggestions(index, newValue);
    console.log(`🔍 [QueryUnit ${index}] Autocomplete for "${newValue}"`);
  };

  const handleAddSuggestion = (value) => {
    if (activeUnitIndex === null) return;

    const updated = [...queryUnits];
    const unit = updated[activeUnitIndex];

    const selectedSet = new Set(unit.selected);
    selectedSet.add(value);
    unit.selected = [...selectedSet];

    setQueryUnits(updated);

    // 🧠 Use shared logic
    updateSuggestions(activeUnitIndex, unit.value);

    console.log(`✅ [QueryUnit ${activeUnitIndex}] Selected "${value}"`);
  };

  const removeSelectedValue = (unitIndex, valueToRemove) => {
    const updated = [...queryUnits];
    updated[unitIndex].selected = updated[unitIndex].selected.filter(v => v !== valueToRemove);
    setQueryUnits(updated);
    console.log(`❌ [QueryUnit ${unitIndex}] Removed "${valueToRemove}"`);
  };

  const addQueryUnit = () => {
    const newUnit = { key: "", value: "", selected: [] };
    setQueryUnits([...queryUnits, newUnit]);
    console.log("➕ Added new Query Unit:", newUnit);
  };

  const removeQueryUnit = (indexToRemove) => {
    setQueryUnits((prev) => prev.filter((_, idx) => idx !== indexToRemove));
    console.log(`❌ Removed Query Unit at index ${indexToRemove}`);
  };

  const queryMatrix = async () => {
    const payload = {
      query_units: queryUnits
        .filter(q => q.key && q.selected.length > 0)
        .map(q => ({ key: q.key, selected: q.selected })),
      row_limit: rowLimit  // 👈 Add this
    };

    try {
      setReadStatus("requesting");
      const start = performance.now(); // ⏱️ Start timer

      const res = await fetch(`${API_BASE}/read/query/${handle}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      const elapsed = ((performance.now() - start) / 1000).toFixed(2); // ⏱️ End timer

      setReadDuration(elapsed); // ✅ Save for later display
      onResult(data);
      setReadStatus("idle");
    } catch (err) {
      console.error("❌ Backend matrix query failed:", err);
      setReadStatus("error");
      setReadDuration(null);
    }
  };

  const handleInputFocus = (index) => {
    setActiveUnitIndex(index);
    const unit = queryUnits[index];
    updateSuggestions(index, unit.value || "");
    console.log(`🖱️ [QueryUnit ${index}] Focused input, showing suggestions`);
  };

  const handleInputBlur = () => {
    blurTimeoutRef.current = setTimeout(() => {
      setSuggestions([]);
      setActiveUnitIndex(null);
      console.log("💤 Input blurred, hiding suggestions");
    }, 100);
  };

  const updateSuggestions = (index, keyword = "") => {
    const unit = queryUnits[index];
    const source = getSourceValues(unit.key) || [];

    const matches = source
      .filter(val => val.toLowerCase().includes(keyword.toLowerCase()))
      .filter(val => !unit.selected.includes(val))
      .sort((a, b) => a.localeCompare(b));

    setAllMatches(matches);
    setVisibleCount(20);
    setSuggestions(matches.slice(0, 20));
  };

  return (
    <div className="bg-white p-4 mt-6 rounded shadow-md">
      <h2 className="text-lg font-semibold mb-2">🔍 Query Expression</h2>

      {queryUnits.map((unit, idx) => (
        <div
          key={idx}
          className="mb-2 transition-all duration-300 ease-in-out transform"
        >
          <div className="opacity-0 animate-fade-in">
            <div className="flex items-start mb-1 justify-between">
              <div className="flex items-start gap-2 w-full">
                <select
                  value={unit.key}
                  onChange={(e) => handleKeyChange(idx, e.target.value)}
                  className="border p-1"
                >
                  <option value="" disabled>Select key</option>
                  {availableKeys.map((k) => (
                    <option key={k} value={k}>{k}</option>
                  ))}
                </select>

                <div className="relative w-3/4">
                  <input
                    type="text"
                    placeholder={`Enter ${unit.key || "value"}`}
                    value={unit.value}
                    onChange={(e) => handleValueChange(idx, e.target.value)}
                    onFocus={() => handleInputFocus(idx)}
                    onBlur={handleInputBlur}
                    className="border p-1 w-full"
                  />

                {idx === activeUnitIndex && suggestions.length > 0 && (
                  <div
                    className="absolute z-10 bg-white border mt-1 rounded shadow w-full overflow-y-auto overflow-x-hidden"
                    style={{ maxHeight: "15rem" }}
                    onScroll={(e) => {
                      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
                      if (scrollTop + clientHeight >= scrollHeight - 10) {
                        const nextCount = visibleCount + 20;
                        setVisibleCount(nextCount);
                        setSuggestions(allMatches.slice(0, nextCount));
                      }
                    }}
                  >
                    {suggestions.map((s, i) => (
                      <div
                        key={i}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          clearTimeout(blurTimeoutRef.current);
                          handleAddSuggestion(s);
                        }}
                        className="p-1 hover:bg-blue-100 cursor-pointer text-sm"
                      >
                        {s}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <button
              onClick={() => idx > 0 && removeQueryUnit(idx)}
              className={`ml-2 text-xl font-bold ${idx > 0 ? "text-red-600 hover:text-red-800" : "invisible"}`}
              title={idx > 0 ? "Remove this query unit" : ""}
            >
              ×
            </button>
          </div>
        </div>

          {unit.selected.length > 0 && (
            <div className="flex flex-wrap">
              {unit.selected.map((val, i) => (
                <div
                  key={i}
                  className="opacity-0 animate-fade-in transition-all duration-300 ease-in-out"
                >
                  <span
                    className="bg-blue-100 text-blue-800 text-sm px-2 py-1 mr-2 mb-1 rounded flex items-center"
                  >
                    {val}
                    <button
                      className="ml-1 text-red-500"
                      onClick={() => removeSelectedValue(idx, val)}
                    >
                      ×
                    </button>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

      ))}

      <div className="mt-4">
        <label className="block mb-1 font-medium text-sm text-gray-700">Maximum number of matching cells to load:</label>
        <input
          type="number"
          value={rowLimit}
          min={1}
          onChange={(e) => setRowLimit(parseInt(e.target.value))}
          className="border p-1 w-24"
        />
      </div>

      <button
        onClick={addQueryUnit}
        className="px-2 py-1 bg-green-600 text-white rounded mt-2"
      >
        ➕ Add Query Unit
      </button>

      <button
        onClick={queryMatrix}
        className="ml-2 px-2 py-1 bg-indigo-600 text-white rounded mt-2"
      >
        Query
      </button>
    </div>
  );
}

export default H5ADReadPanel;
